import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Users, Utensils, CalendarCheck, Phone, PartyPopper } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';

const CateringPage = () => {
  const { t } = useLanguage();

  const cateringMenuData = [
    { 
      name: t("cateringPage.dishes.idly"), 
      description: t("cateringPage.dishes.idlyDesc"),
      isMarked: true,
    },
    { 
      name: t("cateringPage.dishes.vada"), 
      description: t("cateringPage.dishes.vadaDesc"),
      isMarked: true,
    },
    { 
      name: t("cateringPage.dishes.idiyappam"), 
      description: t("cateringPage.dishes.idiyappamDesc"),
      isMarked: true,
    },
    { 
      name: t("cateringPage.dishes.dosa"), 
      description: t("cateringPage.dishes.dosaDesc"),
      isMarked: true,
    },
    { 
      name: t("cateringPage.dishes.loempiaRoll"), 
      description: t("cateringPage.dishes.loempiaRollDesc"),
      isMarked: false,
    },
    { 
      name: t("cateringPage.dishes.kottuRoti"), 
      description: t("cateringPage.dishes.kottuRotiDesc"),
      isMarked: false,
    },
    { 
      name: t("cateringPage.dishes.rijstMetCurry"), 
      description: t("cateringPage.dishes.rijstMetCurryDesc"),
      isMarked: false,
    },
  ];

  const CateringMenuItem = ({ name, description, index, isMarked }) => (
    <motion.div
      className={cn(
        "p-6 rounded-lg shadow-md card-hover border border-gray-100 flex flex-col",
        isMarked ? "bg-primary/10" : "bg-card"
      )}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <h3 className="text-xl font-semibold text-primary mb-2">{name}</h3>
      {description && <p className="text-muted-foreground text-sm flex-grow">{description}</p>}
    </motion.div>
  );

  return (
    <div className="py-12 md:py-20 bg-white pattern-bg">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-12 md:mb-16"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Users className="h-16 w-16 brand-text mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-extrabold brand-text mb-4">{t('cateringPage.title')}</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('cateringPage.subtitle')}
          </p>
        </motion.div>

        <section className="mb-16 md:mb-20">
          <motion.h2 
            className="text-3xl md:text-4xl font-bold text-gray-800 text-center mb-4"
            initial={{ opacity: 0, y:20}}
            whileInView={{ opacity:1, y:0}}
            viewport={{once: true}}
            transition={{duration:0.5, delay: 0.1}}
          >
            {t('cateringPage.menuTitle')}
          </motion.h2>
          <motion.div 
            className="text-center p-6 bg-primary/10 rounded-lg mb-10 max-w-2xl mx-auto"
            initial={{ opacity: 0, y:20}}
            whileInView={{ opacity:1, y:0}}
            viewport={{once: true}}
            transition={{duration:0.5, delay: 0.2}}
          >
            <Utensils className="h-8 w-8 brand-text mx-auto mb-3" />
            <p className="text-md font-semibold text-gray-700">
              {t('cateringPage.markedDishesNote')}
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
            {cateringMenuData.map((item, index) => (
              <CateringMenuItem key={index} name={item.name} description={item.description} index={index} isMarked={item.isMarked} />
            ))}
          </div>
        </section>
        
        <section className="mb-16 md:mb-20">
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              className="rounded-lg overflow-hidden shadow-lg"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <img  alt={t('cateringPage.image1Alt')} className="w-full h-80 md:h-[450px] object-cover" src="/images/catering/catering-dishes-1.jpg" />
            </motion.div>
            <motion.div
              className="rounded-lg overflow-hidden shadow-lg"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <img  alt={t('cateringPage.image2Alt')} className="w-full h-80 md:h-[450px] object-cover" src="/images/catering/catering-dishes-2.jpg" />
            </motion.div>
          </div>
        </section>

        <motion.div 
          className="mb-12 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.55 }}
        >
          <PartyPopper className="h-10 w-10 text-amber-500 mx-auto mb-3" />
          <p className="text-muted-foreground">
            {t('cateringPage.eventsTitle')}
          </p>
        </motion.div>

        <motion.div
          className="bg-secondary p-8 md:p-12 rounded-xl shadow-lg text-center"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <CalendarCheck className="h-12 w-12 brand-text mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-gray-800 mb-4">{t('cateringPage.planEventTitle')}</h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto whitespace-pre-line">
            {t('cateringPage.planEventText')}
          </p>
          <Link to="/contact">
            <Button size="lg" className="primary-button">
              <Phone className="mr-2 h-5 w-5" /> {t('cateringPage.contactButton')}
            </Button>
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default CateringPage;